import React, { useEffect, useRef, useState } from "react";
import Collapse from "react-bootstrap/Collapse";
import Select from "react-select";
import Swal from "sweetalert2";
import Loader from "../Loader/loader";
import CashEntryDetailsModal from "./CashEntryDetailsModal";

import CashPassbookViewerServices from
  "../../../services/CashPassbookViewerServices";
import Pagination from "../Pagenation/Pagenation";

const readData = (response) => {
  const body = response?.data?.status
    ? response.data
    : response;

  return body?.data;
};

const toInitCap = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/\b[a-z]/g, (letter) => letter.toUpperCase());

const getAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

const formatEntryBalance = (value) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  return formatAmount(value);
};

const formatAmount = (value) =>
  getAmount(value).toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

const formatDate = (value) => {
  if (!value) return null;

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return null;

  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  }).formatToParts(date);

  const values = Object.fromEntries(
    parts.map(({ type, value: partValue }) => [
      type,
      partValue
    ])
  );

  return {
    date: `${values.day} ${values.month}, ${values.year}`,
    time: `${values.hour}:${values.minute} ${values.dayPeriod.toUpperCase()}`
  };
};

const statusLabels = {
  pendingAdmin: "Pending CashChecker",
  pendingAccounts: "Pending Accounts",
  approved: "Approved",
  rejectedByAdmin: "Rejected by CashChecker",
  rejectedByAccounts: "Rejected by Accounts"
};

const statusColors = {
  pendingAdmin: "bg-warning text-dark",
  pendingAccounts: "bg-primary",
  approved: "bg-success",
  rejectedByAdmin: "bg-danger",
  rejectedByAccounts: "bg-danger"
};

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "38px",
    fontSize: "13px"
  }),
  menu: (base) => ({
    ...base,
    zIndex: 20
  })
};

const emptyPagination = {
  total: 0,
  totalPages: 1
};

const showError = (error, fallback) =>
  Swal.fire(
    "Error",
    error?.response?.data?.message ||
    error?.message ||
    fallback,
    "error"
  );

const SectionToggle = ({
  open,
  onClick,
  controls,
  label
}) => (
  <button
    type="button"
    className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2"
    onClick={onClick}
    aria-expanded={open}
    aria-controls={controls}
    aria-label={`${open ? "Hide" : "Show"} ${label}`}
    title={`${open ? "Hide" : "Show"} ${label}`}
    style={{
      flexShrink: 0,
      whiteSpace: "nowrap",
      fontSize: "12px",
      padding: "5px 10px"
    }}
  >
    <i
      className={`fas ${open ? "fa-angle-up" : "fa-angle-down"
        }`}
      aria-hidden="true"
    />

    {open ? "Hide" : "Show"}
  </button>
);

const CashPassbookViewer = () => {
  const [searchOpen, setSearchOpen] = useState(true);
  const [dataOpen, setDataOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  const [stores, setStores] = useState([]);
  const [passbooks, setPassbooks] = useState([]);
  const [storeId, setStoreId] = useState("");
  const [passbookId, setPassbookId] = useState("");

  const [passbook, setPassbook] = useState(null);
  const [entries, setEntries] = useState([]);

  const [storesLoading, setStoresLoading] = useState(false);
  const [passbooksLoading, setPassbooksLoading] =
    useState(false);
  const [detailsLoading, setDetailsLoading] =
    useState(false);
  const [detailsError, setDetailsError] = useState("");

  const [entryType, setEntryType] = useState("");
  const [status, setStatus] = useState("");

  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [vendorId, setVendorId] = useState("");

  const [categoryFilters, setCategoryFilters] = useState([]);
  const [vendorFilters, setVendorFilters] = useState([]);

  // Existing Pagination uses zero-based pages.
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] =
    useState(emptyPagination);

  // Automatically change sections only when a newly selected
  // passbook loads, not on every filter/page/refresh request.
  const revealSelectedPassbook = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const loadStores = async () => {
      setStoresLoading(true);

      try {
        const response =
          await CashPassbookViewerServices.getStores();
        const data = readData(response);

        if (!Array.isArray(data?.stores)) {
          throw new Error("Unexpected store API response.");
        }

        if (!cancelled) {
          setStores(data.stores);
        }
      } catch (error) {
        if (!cancelled) {
          showError(error, "Unable to load stores.");
        }
      } finally {
        if (!cancelled) setStoresLoading(false);
      }
    };

    loadStores();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    setPassbooks([]);

    if (!storeId) {
      setPassbooksLoading(false);
      return;
    }

    const loadPassbooks = async () => {
      setPassbooksLoading(true);

      try {
        const response =
          await CashPassbookViewerServices.getPassbooks(
            storeId
          );
        const data = readData(response);

        if (!Array.isArray(data?.passbooks)) {
          throw new Error("Unexpected passbook API response.");
        }

        if (!cancelled) {
          setPassbooks(data.passbooks);
        }
      } catch (error) {
        if (!cancelled) {
          showError(error, "Unable to load passbooks.");
        }
      } finally {
        if (!cancelled) setPassbooksLoading(false);
      }
    };

    loadPassbooks();

    return () => {
      cancelled = true;
    };
  }, [storeId]);

  useEffect(() => {
    let cancelled = false;

    if (!storeId || !passbookId) {
      setDetailsLoading(false);
      return;
    }

    const loadDetails = async () => {
      setDetailsLoading(true);
      setDetailsError("");

      try {
        const response =
          await CashPassbookViewerServices.getPassbookDetails(
            passbookId,
            {
              storeId,
              entryType,
              status,
              categoryId,
              vendorId,
              search,
              page: page + 1,
              limit: pageSize
            }
          );

        const data = readData(response);

        if (
          !data?.passbook ||
          !Array.isArray(data?.entries) ||
          !data?.pagination
        ) {
          throw new Error(
            "Unexpected passbook details API response."
          );
        }

        if (!cancelled) {
          setPassbook(data.passbook);
          setEntries(data.entries);
          setCategoryFilters(data.filterOptions?.categories || []);
          setVendorFilters(data.filterOptions?.vendors || []);
          setPagination(data.pagination);

          const returnedPage = data.pagination.page - 1;

          if (returnedPage !== page) {
            setPage(returnedPage);
          }

          if (revealSelectedPassbook.current) {
            setSearchOpen(false);
            setDataOpen(true);
            revealSelectedPassbook.current = false;
          }
        }
      } catch (error) {
        if (!cancelled) {
          const message =
            error?.response?.data?.message ||
            error?.message ||
            "Unable to load passbook details.";

          setPassbook(null);
          setEntries([]);
          setPagination(emptyPagination);
          setDetailsError(message);
          setSearchOpen(true);

          showError(error, "Unable to load passbook details.");
        }
      } finally {
        if (!cancelled) setDetailsLoading(false);
      }
    };

    loadDetails();

    return () => {
      cancelled = true;
    };
  }, [
    storeId,
    passbookId,
    entryType,
    status,
    categoryId,
    vendorId,
    search,
    page,
    pageSize
  ]);

  const clearDetails = () => {
    setPassbook(null);
    setEntries([]);
    setDetailsError("");
    setPagination(emptyPagination);
    setPage(0);
  };

  const changeStore = (selected) => {
    revealSelectedPassbook.current = false;

    setStoreId(selected?.value || "");
    setPassbookId("");
    setPassbooks([]);
    setEntryType("");
    setStatus("");
    setSearchText("");
    setSearch("");
    setCategoryId("");
    setVendorId("");
    setCategoryFilters([]);
    setVendorFilters([]);

    clearDetails();
    setSearchOpen(true);
    setDataOpen(false);
  };

  const changePassbook = (selected) => {
    const selectedId = selected?.value || "";

    revealSelectedPassbook.current = Boolean(selectedId);

    setPassbookId(selectedId);
    setEntryType("");
    setStatus("");
    setSearchText("");
    setSearch("");
    setCategoryId("");
    setVendorId("");
    setCategoryFilters([]);
    setVendorFilters([]);

    clearDetails();
    setSearchOpen(true);
    setDataOpen(false);
  };

  const storeOptions = stores.map((store) => ({
    value: String(store._id),
    label: `${toInitCap(store.name)}${store.location
      ? ` — ${toInitCap(store.location)}`
      : ""
      }`
  }));

  const passbookOptions = passbooks.map((item) => ({
    value: String(item._id),
    label: `${toInitCap(item.name)}${item.status === "inactive" ? " (Inactive)" : ""
      }`
  }));

  const balanceItems = [
    {
      key: "in",
      label: "Cash In",
      value: passbook?.totalIn,
      symbol: "+",
      className: "cash-passbook-icon-in"
    },
    {
      key: "out",
      label: "Cash Out",
      value: passbook?.totalOut,
      symbol: "−",
      className: "cash-passbook-icon-out"
    },
    {
      key: "balance",
      label: "Net Balance",
      value: passbook?.balance,
      symbol: "=",
      className: "cash-passbook-icon-balance"
    }
  ];

  const isFetching =
    storesLoading || passbooksLoading || detailsLoading;

  return (
    <div
      className="row"
      aria-busy={isFetching}
      style={{
        position: "relative",
        minHeight: "200px"
      }}
    >
      {isFetching && (
        <div
          role="status"
          aria-label="Loading cash passbook data"
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 100,
            backgroundColor: "rgba(255, 255, 255, 0.65)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "10px",
            cursor: "wait"
          }}
        >
          <Loader />
        </div>
      )}

      {/* Keep all your existing sections below */}
      <div className="col-12">
        {/* Combined heading and store/passbook search */}
        <div className="filter cm-content-box box-primary cash-viewer-search-card">
          <div className="content-title">
            <div className="row g-2 align-items-center w-100 m-0">
              {/* Left: heading */}
              <div className="col-12 col-md-3">
                <div className="cpa">
                  <i
                    className="fa fa-book me-2"
                    aria-hidden="true"
                  />
                  Cash Passbook
                </div>
              </div>

              {/* Middle: instruction */}
              <div className="col-12 col-md-6 text-md-center">
                <span className="cash-viewer-search-hint">
                  Select a store and passbook to view details.
                </span>
              </div>

              {/* Right: show/hide search */}
              <div className="col-12 col-md-3 d-flex justify-content-end">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => setSearchOpen((value) => !value)}
                  aria-expanded={searchOpen}
                  aria-controls="cash-passbook-search"
                >
                  <i
                    className={`fa ${searchOpen ? "fa-chevron-up" : "fa-chevron-down"
                      } me-2`}
                    aria-hidden="true"
                  />
                  {searchOpen ? "Hide Search" : "Show Search"}
                </button>
              </div>
            </div>
          </div>

          <Collapse in={searchOpen}>
            <div
              id="cash-passbook-search"
              className="cm-content-body form excerpt"
            >
              <div className="card-body">
                <div className="row g-3">
                  {/* Store */}
                  <div className="col-12 col-md-6">
                    <label
                      htmlFor="viewer-store"
                      className="form-label"
                    >
                      Store / Location
                    </label>

                    <Select
                      inputId="viewer-store"
                      options={storeOptions}
                      value={
                        storeOptions.find(
                          (option) => option.value === storeId
                        ) || null
                      }
                      onChange={changeStore}
                      placeholder="Select Store / Location"
                      isSearchable
                      isClearable
                      isLoading={storesLoading}
                      isDisabled={storesLoading}
                      noOptionsMessage={() =>
                        "No active stores available"
                      }
                      styles={selectStyles}
                      className="custom-react-select"
                    />
                  </div>

                  {/* Passbook */}
                  <div className="col-12 col-md-6">
                    <label
                      htmlFor="viewer-passbook"
                      className="form-label"
                    >
                      Passbook
                    </label>

                    <Select
                      inputId="viewer-passbook"
                      options={passbookOptions}
                      value={
                        passbookOptions.find(
                          (option) => option.value === passbookId
                        ) || null
                      }
                      onChange={changePassbook}
                      placeholder={
                        !storeId
                          ? "Select Store First"
                          : passbooksLoading
                            ? "Loading Passbooks..."
                            : "Select Passbook"
                      }
                      isSearchable
                      isClearable
                      isLoading={passbooksLoading}
                      isDisabled={!storeId || passbooksLoading}
                      noOptionsMessage={() =>
                        "No passbook available for this store"
                      }
                      styles={selectStyles}
                      className="custom-react-select"
                    />
                  </div>
                </div>

                {detailsError && (
                  <div className="alert alert-danger mt-3 mb-0">
                    {detailsError}
                  </div>
                )}
              </div>
            </div>
          </Collapse>
        </div>

        {/* SECTION 3: BALANCES AND TRANSACTIONS */}
        {
          passbook && (
            <div className="filter cm-content-box box-primary">
              <div className="content-title">
                <div className="row g-2 align-items-center w-100 m-0">
                  {/* Left: heading */}
                  <div className="col-12 col-md-4">
                    <div className="cpa">
                      <i
                        className="fa fa-list me-2"
                        aria-hidden="true"
                      />
                      Passbook Transactions

                      <span className="badge bg-orange ms-2">
                        {pagination.total}
                      </span>
                    </div>
                  </div>

                  {/* Middle: cashbook name */}
                  <div className="col-12 col-md-4 text-md-center">
                    <div className="cpa d-flex align-items-center justify-content-md-center flex-wrap gap-2">
                      <span style={{ overflowWrap: "anywhere" }}>
                        {toInitCap(passbook?.name || "—")}
                      </span>

                      {passbook?.status && (
                        <span
                          className={`badge ${passbook.status === "active"
                            ? "bg-success"
                            : "bg-secondary"
                            }`}
                          style={{
                            fontSize: "10px",
                            fontWeight: 600,
                            lineHeight: 1.2,
                            padding: "4px 8px",
                            borderRadius: "4px",
                            color: "#fff",
                            boxShadow: "0 2px 5px rgba(0, 0, 0, 0.15)",
                            whiteSpace: "nowrap",
                            flexShrink: 0
                          }}
                        >
                          {toInitCap(passbook.status)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Right: toggle */}
                  <div className="col-12 col-md-4 d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => setDataOpen((value) => !value)}
                      aria-expanded={dataOpen}
                      aria-controls="cash-passbook-data"
                    >
                      <i
                        className={`fa ${dataOpen ? "fa-chevron-up" : "fa-chevron-down"
                          } me-2`}
                        aria-hidden="true"
                      />
                      {dataOpen ? "Hide Passbook" : "Show Passbook"}
                    </button>
                  </div>
                </div>
              </div>

              <Collapse in={dataOpen}>
                <div
                  id="cash-passbook-data"
                  className="cm-content-body form excerpt"
                >
                  <div className="card-body">
                    <div className="cash-viewer-summary cash-viewer-summary-three mb-3">
                      {balanceItems.map((item) => (
                        <div
                          key={item.key}
                          className={`cash-viewer-summary-box ${item.key === "in"
                              ? "cash-viewer-summary-green"
                              : item.key === "out"
                                ? "cash-viewer-summary-blue"
                                : "cash-viewer-summary-purple"
                            }`}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <span
                              className="cash-viewer-summary-icon"
                              aria-hidden="true"
                            >
                              {item.symbol}
                            </span>

                            <div style={{ minWidth: 0 }}>
                              <div className="cash-viewer-summary-value">
                                {getAmount(item.value).toLocaleString("en-IN", {
                                  maximumFractionDigits: 2
                                })}
                              </div>

                              <div className="cash-viewer-summary-label mt-1">
                                {item.label}
                              </div>
                            </div>
                          </div>

                          <div className="cash-viewer-summary-note mt-2">
                            *** Totals include approved entries only.
                          </div>
                        </div>
                      ))}
                    </div>



                    <div className="row g-2 align-items-end cash-passbook-compact-filters cash-viewer-filter-panel">
                      {/* Search */}
                      <div className="col-12 col-md-6 col-xl-3">
                        <label htmlFor="viewer-search" className="form-label">
                          Search
                        </label>

                        <div className="input-group">
                          <input
                            id="viewer-search"
                            type="search"
                            className="form-control"
                            placeholder="Amount / CashMaker remark"
                            value={searchText}
                            maxLength={200}
                            disabled={detailsLoading}
                            onChange={(event) => {
                              const value = event.target.value;
                              setSearchText(value);

                              if (!value.trim()) {
                                setSearch("");
                                setPage(0);
                              }
                            }}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                                setSearch(searchText.trim());
                                setPage(0);
                              }
                            }}
                          />

                          <button
                            type="button"
                            className="btn btn-primary"
                            disabled={detailsLoading}
                            onClick={() => {
                              setSearch(searchText.trim());
                              setPage(0);
                            }}
                            aria-label="Search transactions"
                          >
                            <i className="fa fa-search" aria-hidden="true" />
                          </button>
                        </div>
                      </div>

                      {/* Category */}
                      <div className="col-12 col-md-6 col-xl-2">
                        <label htmlFor="viewer-category" className="form-label">
                          Category
                        </label>

                        <select
                          id="viewer-category"
                          className="form-control"
                          value={categoryId}
                          disabled={detailsLoading}
                          onChange={(event) => {
                            setCategoryId(event.target.value);
                            setPage(0);
                          }}
                        >
                          <option value="">All Categories</option>

                          {categoryFilters.map((category) => (
                            <option key={category._id} value={category._id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Vendor */}
                      <div className="col-12 col-md-6 col-xl-2">
                        <label htmlFor="viewer-vendor" className="form-label">
                          Vendor
                        </label>

                        <select
                          id="viewer-vendor"
                          className="form-control"
                          value={vendorId}
                          disabled={detailsLoading}
                          onChange={(event) => {
                            setVendorId(event.target.value);
                            setPage(0);
                          }}
                        >
                          <option value="">All Vendors</option>

                          {vendorFilters.map((vendor) => (
                            <option key={vendor._id} value={vendor._id}>
                              {vendor.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Entry Type */}
                      <div className="col-12 col-md-6 col-xl-2">
                        <label htmlFor="viewer-entry-type" className="form-label">
                          Entry Type
                        </label>

                        <select
                          id="viewer-entry-type"
                          className="form-control"
                          value={entryType}
                          disabled={detailsLoading}
                          onChange={(event) => {
                            setEntryType(event.target.value);
                            setPage(0);
                          }}
                        >
                          <option value="">All Entries</option>
                          <option value="in">Cash In</option>
                          <option value="out">Cash Out</option>
                        </select>
                      </div>

                      {/* Status */}
                      <div className="col-12 col-md-8 col-xl-2">
                        <label htmlFor="viewer-status" className="form-label">
                          Status
                        </label>

                        <select
                          id="viewer-status"
                          className="form-control"
                          value={status}
                          disabled={detailsLoading}
                          onChange={(event) => {
                            setStatus(event.target.value);
                            setPage(0);
                          }}
                        >
                          <option value="">All Statuses</option>

                          {Object.entries(statusLabels).map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Rows and reset stay together */}
                      <div className="col-12 col-md-4 col-xl-1">
                        <label htmlFor="viewer-page-size" className="form-label">
                          Rows
                        </label>

                        <div className="d-flex align-items-center gap-1">
                          <select
                            id="viewer-page-size"
                            className="form-control"
                            style={{ minWidth: 0, flex: "1 1 0" }}
                            value={pageSize}
                            disabled={detailsLoading}
                            onChange={(event) => {
                              setPageSize(Number(event.target.value));
                              setPage(0);
                            }}
                          >
                            {[10, 20, 50, 100].map((size) => (
                              <option key={size} value={size}>
                                {size}
                              </option>
                            ))}
                          </select>

                          {Boolean(
                            search || categoryId || vendorId || entryType || status
                          ) && (
                              <button
                                type="button"
                                className="btn btn-outline-danger px-2 flex-shrink-0"
                                disabled={detailsLoading}
                                onClick={() => {
                                  setSearchText("");
                                  setSearch("");
                                  setCategoryId("");
                                  setVendorId("");
                                  setEntryType("");
                                  setStatus("");
                                  setPage(0);
                                }}
                                title="Reset filters"
                                aria-label="Reset all transaction filters"
                              >
                                <i className="fa fa-times" aria-hidden="true" />
                              </button>
                            )}
                        </div>
                      </div>
                    </div>
                    <div className="table-responsive cash-viewer-table-panel">
                      <div
                        id="cashbook_content_wrapper"
                        className="dataTables_wrapper no-footer"
                      >
                        <div
                          style={{
                            overflowX: "auto",
                            overflowY: "auto",
                            maxHeight: "400px",
                            whiteSpace: "nowrap",
                            position: "relative",
                            zIndex: 0
                          }}
                        >
                          <table
                            id="cashbook-transactions-data"
                            className="table table-bordered table-responsive-lg table-striped table-condensed flip-content"
                          >
                            <thead className="cash-viewer-table-header">
                              <tr>
                                <th>S.No</th>
                                <th>Date & Time</th>
                                <th>Details</th>
                                <th>Type</th>
                                <th>Category</th>
                                <th>Mode</th>
                                <th>Bills</th>
                                <th>Amount</th>
                                <th>Balance</th>
                                <th>Balance Before</th>
                                <th>Status</th>
                                <th>Vendor</th>
                                <th>Voucher / Transaction ID</th>
                                <th>Remarks</th>
                              </tr>
                            </thead>

                            <tbody>
                              {detailsLoading ? null : entries.length === 0 ? (
                                <tr>
                                  <td colSpan={14} className="text-center py-4">
                                    No transactions found.
                                  </td>
                                </tr>
                              ) : (
                                entries.map((entry, index) => (
                                  <tr key={entry._id}>
                                    <td>{page * pageSize + index + 1}</td>

                                    <td className="text-nowrap">
                                      {(() => {
                                        const formatted = formatDate(entry.dateTime);

                                        if (!formatted) return "—";

                                        return (
                                          <div className="cash-entry-date-time">
                                            <div>{formatted.date}</div>
                                            <div className="cash-entry-time">
                                              {formatted.time}
                                            </div>
                                          </div>
                                        );
                                      })()}
                                    </td>

                                    <td
                                      className="cash-transaction-details-cell cash-entry-details-clickable"
                                      onClick={() => setSelectedEntry(entry)}
                                    >
                                      <div className="cash-transaction-details">
                                        {entry.adminReviewedBy?.name && (
                                          <div className="cash-transaction-details-meta">
                                            {entry.status === "rejectedByAdmin"
                                              ? "Rejected By:"
                                              : "Approved By:"}{" "}
                                            {entry.adminReviewedBy.name} (CashChecker)
                                          </div>
                                        )}

                                        {entry.accountsReviewedBy?.name && (
                                          <div className="cash-transaction-details-meta">
                                            {entry.status === "rejectedByAccounts"
                                              ? "Rejected By:"
                                              : "Approved By:"}{" "}
                                            {entry.accountsReviewedBy.name} (Accounts)
                                          </div>
                                        )}

                                        <div
                                          className="cash-transaction-details-remark"
                                          title={entry.remark || ""}
                                        >
                                          {entry.remark || "—"}
                                        </div>

                                        <div className="cash-transaction-details-meta">
                                          by{" "}
                                          {entry.createdBy?.name ||
                                            entry.employee?.name ||
                                            passbook?.employee?.name ||
                                            "—"}
                                        </div>
                                      </div>

                                      <button
                                        type="button"
                                        className="cash-entry-details-open-button"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          setSelectedEntry(entry);
                                        }}
                                        aria-label={`View entry details for ${entry.voucherNo || "cash entry"
                                          }`}
                                      >
                                        View Details
                                      </button>
                                    </td>

                                    <td>
                                      <span
                                        className={`badge ${entry.entryType === "in"
                                          ? "bg-success"
                                          : "bg-warning text-dark"
                                          }`}
                                      >
                                        {entry.entryType === "in" ? "Cash In" : "Cash Out"}
                                      </span>
                                    </td>

                                    <td className="scrollable-description">
                                      {entry.category?.name || "—"}
                                    </td>

                                    {/* Mode */}
                                    <td>{toInitCap(entry.paymentMode || "—")}</td>

                                    {/* Bills */}
                                    <td className="scrollable-description">
                                      {entry.image?.length
                                        ? entry.image.map((filename, fileIndex) => (
                                          <div key={`${filename}-${fileIndex}`}>
                                            <i
                                              className="fa fa-paperclip me-1"
                                              aria-hidden="true"
                                            />
                                            {filename}
                                          </div>
                                        ))
                                        : "—"}
                                    </td>

                                    {/* Amount */}
                                    <td className="text-nowrap">
                                      {formatAmount(entry.amount)}
                                    </td>

                                    {/* Balance */}
                                    <td className="text-nowrap">
                                      {formatEntryBalance(entry.balanceAfter)}
                                    </td>

                                    {/* Balance Before */}
                                    <td className="text-nowrap">
                                      {formatEntryBalance(entry.balanceBefore)}
                                    </td>

                                    {/* Status */}
                                    <td>
                                      <span
                                        className={`badge ${statusColors[entry.status] || "bg-secondary"
                                          }`}
                                      >
                                        {statusLabels[entry.status] || entry.status}
                                      </span>
                                    </td>

                                    {/* Vendor */}
                                    <td className="scrollable-description">
                                      <div>{entry.vendor?.name || "—"}</div>

                                      {entry.vendor?.mobileNo && (
                                        <div className="small text-muted mt-1">
                                          {entry.vendor.mobileNo}
                                        </div>
                                      )}
                                    </td>

                                    {/* Voucher / Transaction ID */}
                                    <td className="scrollable-description">
                                      {entry.voucherNo || "—"}
                                    </td>

                                    {/* Combined Remarks */}
                                    <td className="cash-entry-combined-remarks">
                                      <div>
                                        <strong>CashMaker: </strong>
                                        {entry.remark || "—"}
                                      </div>

                                      <div>
                                        <strong>CashChecker: </strong>
                                        {entry.adminRemark || "—"}
                                      </div>

                                      <div>
                                        <strong>Accounts: </strong>
                                        {entry.accountsRemark || "—"}
                                      </div>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>

                        {!detailsLoading && (
                          <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">
                            <div className="dataTables_info">
                              {pagination.total > 0 ? (
                                <>
                                  Showing {page * pageSize + 1} to{" "}
                                  {Math.min(
                                    pagination.total,
                                    page * pageSize + entries.length
                                  )}{" "}
                                  of {pagination.total} entries
                                </>
                              ) : (
                                <>Showing 0 to 0 of 0 entries</>
                              )}
                            </div>

                            {pagination.total > 0 && (
                              <Pagination
                                activePage={page}
                                totalPages={pagination.totalPages}
                                onPageChange={(nextPage) => {
                                  if (
                                    detailsLoading ||
                                    !Number.isInteger(nextPage)
                                  ) {
                                    return;
                                  }

                                  setPage(
                                    Math.max(
                                      0,
                                      Math.min(
                                        nextPage,
                                        pagination.totalPages - 1
                                      )
                                    )
                                  );
                                }}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Collapse>
            </div>
          )
        }
      </div>

      <CashEntryDetailsModal
        entry={selectedEntry}
        passbook={passbook}
        handleClose={() => setSelectedEntry(null)}
      />
    </div>
  );
};

export default CashPassbookViewer;